import type { LeaguePlayer } from '../model/types';

const LEAGUE_TIME_ZONE = 'Europe/Moscow';
const EN_DASH = '–';
const MINUS_SIGN = '−';

/**
 * Drops literal parts at the edges of a formatted date, e.g. the Russian year suffix « г.»,
 * so «30 сентября 2026 г.» reads «30 сентября 2026».
 */
const formatDateParts = (iso: string, locale: string, options: Intl.DateTimeFormatOptions): string => {
  const parts = new Intl.DateTimeFormat(locale, { ...options, timeZone: LEAGUE_TIME_ZONE }).formatToParts(
    new Date(iso),
  );
  const firstValueIndex = parts.findIndex((part) => part.type !== 'literal');
  const lastValueIndex = parts.length - 1 - [...parts].reverse().findIndex((part) => part.type !== 'literal');

  return parts
    .slice(firstValueIndex, lastValueIndex + 1)
    .map((part) => part.value)
    .join('');
};

export const formatLeagueNumber = (value: number, locale = 'ru'): string => new Intl.NumberFormat(locale).format(value);

export const formatRecord = (wins: number, losses: number): string => `${wins}${EN_DASH}${losses}`;

export const formatSets = (won: number, lost: number): string => `${won}:${lost}`;

export const formatSetDiff = (won: number, lost: number): string => {
  const diff = won - lost;

  if (diff > 0) return `+${diff}`;
  if (diff < 0) return `${MINUS_SIGN}${Math.abs(diff)}`;

  return '0';
};

/** «Анна К.», or just «Анна» when the roster publishes no last-name initial. */
export const formatLeaguePlayerName = ({ firstName, lastName }: Pick<LeaguePlayer, 'firstName' | 'lastName'>): string =>
  lastName ? `${firstName} ${lastName}` : firstName;

export const formatLeagueDate = (iso: string, locale: string): string =>
  formatDateParts(iso, locale, { day: 'numeric', month: 'long', year: 'numeric' });

export const formatMatchDateTime = (iso: string, locale: string): string => {
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: LEAGUE_TIME_ZONE,
  }).format(new Date(iso));

  return `${formatLeagueDate(iso, locale)} · ${time}`;
};

export const formatMonthYear = (iso: string, locale: string): string =>
  formatDateParts(iso, locale, { month: 'long', year: 'numeric' });
